/**
 * Provides accessible modal dialog functionality with overlay management and keyboard navigation.
 *
 * This class manages modal dialogs throughout the application, handling show/hide functionality,
 * overlay creation, focus management, and accessibility features. Supports both programmatic
 * and click-based modal activation with proper ARIA attributes and custom events.
 *
 * @remarks
 * - Automatically finds and sets up elements with `.modal` class
 * - Creates overlay element if not present in the DOM
 * - Manages focus states and body scroll locking
 * - Supports deep linking via URL hash fragments
 * - Dispatches custom events for modal show/hide actions
 * - Handles click-outside-to-close functionality
 * - Supports custom callback functions via `data-modal-callback` attribute
 *
 * @example
 * // Expected HTML structure:
 * // <button class="open-modal" href="#myModal" data-modal-callback="myCallback">Open Modal</button>
 * // <div class="modal" id="myModal">
 * //   <div class="modal-content">
 * //     <button class="close-modal">×</button>
 * //     <p>Modal content here</p>
 * //   </div>
 * // </div>
 *
 * // Callback function (must be globally accessible):
 * // function myCallback(triggerElement, modalElement) {
 * //   // Custom logic to populate or modify modal before showing
 * //   return true; // Return true to show modal, false to prevent showing
 * // }
 *
 * // Initialization:
 * const modalPlugin = new modals();
 *
 * // Programmatic control:
 * modalPlugin.show('myModal');
 * modalPlugin.close('myModal');
 *
 * @public
 */
export class modals {
  modals: Array<any> = [];
  modalOverlay: any;
  modalButtons: any;
  activeModal: any = null;
  options = {};
  /**
   * Creates a new modals instance and initializes modal functionality.
   *
   * @param opts - Configuration object for modal behavior (currently unused, reserved for future options)
   */
  constructor(opts: any = {}) {
    /* console.log("Hi Modal!"); */
    let defaults = {};

    this.options = { ...defaults, ...opts };
    this.setupModals();
  }

  /**
   * Programmatically shows a modal by its ID.
   *
   * @param modalId - The ID of the modal element to show (without # prefix)
   * @public
   */
  show = (modalId: string) => {
    this.setupModals();
    if (modalId) {
      this.showModal(modalId, true, null);
    };
  }

  /**
   * Programmatically closes a modal by its ID.
   *
   * @param modalId - The ID of the modal element to close (without # prefix)
   * @public
   */
  close = (modalId: string) => {
    //console.log(`modal.init`);
    this.setupModals();
    if (modalId) {
      this.showModal(modalId, false, null);
    };
  }
  setupModals = () => {

    this.modalOverlay = document.querySelector(".modal-overlay") || this.createOverlay();

    this.modalButtons = document.querySelectorAll(".open-modal");

    this.modals = [...document.querySelectorAll(".modal")];
    this.modals.forEach((m) => {
      m.hidden = "hidden";
      m.role = "dialog";
      m.tabindex = "-1";
    });

    this.modalButtons.forEach((btn: any, i: number) => {
      btn.onclick = (e: Event) => {
        e.preventDefault();
        this.showModal(btn.href, true, btn);
        return false;
      }
    });

    this.modalOverlay.onclick = (e: Event) => {
      //console.log("overlay click");
      e.preventDefault();
      e.stopPropagation();

      console.log(this.activeModal);

      if (this.activeModal) {
        this.showModal(`#${this.activeModal.id}`, false, null);
      }

      return false;
    }

  }
  showModal = (target: string, show: boolean = true, triggerElement: HTMLElement | null = null) => {
    //console.log(show);
    if (!show) {
      //console.log("close it");
    }
    let id = "";
    if (target.indexOf(window.location.origin) > -1) {
      //this is a url
      let url = new URL(target);
      //console.log(url);
      //console.log(url.hash);
      id = url.hash;
    } else {
      //this is a regular elementId
      id = target;
    }

    //console.log(`id: ${id}`);

    let modalTarget = document.getElementById(id.replace("#", ""));
    if (modalTarget) {
      if (show === true) {

        // Execute callback function if specified
        if (triggerElement && this.executeCallback(triggerElement, modalTarget)) {
          // Callback executed successfully, continue with showing modal
        }

        this.activeModal = modalTarget;

        modalTarget.classList.add("modal--show-modal", "show-modal");
        modalTarget.removeAttribute("hidden");
        modalTarget.ariaHidden = "false";
        modalTarget.tabIndex = 0;
        modalTarget.focus();

        let btnClose: HTMLElement | null = modalTarget.querySelector(".close-modal");
        if (btnClose) {
          btnClose!.onclick = (e) => {
            e.preventDefault();
            this.showModal(id, false, null);
            return false;
          }
        }


        //raise onShow event
        //modalTarget.dispatchEvent(modal.onShow);
        modalTarget.dispatchEvent(new CustomEvent("on.modal.show", {
          detail: {
            modal: this.activeModal,
            message: "modal shown",
            target: window.event?.target || null,
            event: window.event || null
          }
        }));

      } else {
        this.activeModal = null;

        modalTarget.classList.remove("modal--show-modal", "show-modal");
        modalTarget.hidden = true;
        modalTarget.ariaHidden = "true";
        modalTarget.tabIndex = -1;

        //raise onClose event
        modalTarget.dispatchEvent(this.onClose);
      }
    }

    if (show === true) {
      document.body.classList.add("modal-open");
      document.documentElement.classList.add("modal-open");

      this.modalOverlay.classList.add("modal__overlay--show-modal", "show-modal");


    } else {
      document.body.classList.remove("modal-open");
      document.documentElement.classList.remove("modal-open");

      this.modalOverlay.classList.remove("modal__overlay--show-modal", "show-modal");
    }
  }

  onShow = new CustomEvent("on.modal.show", {
    detail: {
      modal: this.activeModal,
      message: "modal shown"
    }
  });
  onClose = new CustomEvent("on.modal.close", {
    detail: {
      modal: this.activeModal,
      message: "modal closed"
    }
  });

  createOverlay = () => {

    let div = document.createElement("div");
    div.classList.add("modal-overlay");
    div.tabIndex = -1;

    document.body.appendChild(div);

    return div;
  }

  /**
   * Executes a callback function specified on the trigger element.
   *
   * @param triggerElement - The element that triggered the modal
   * @param modalElement - The modal element being shown
   * @returns boolean - True if callback executed successfully or no callback specified, false if callback failed
   * @private
   */
  executeCallback = (triggerElement: HTMLElement, modalElement: HTMLElement): boolean => {
    try {
      // Check for callback function name in data attribute
      const callbackName = triggerElement.getAttribute('data-modal-callback');

      if (callbackName) {
        // Try to find the function in the global scope
        const callbackFunction = (window as any)[callbackName];

        if (typeof callbackFunction === 'function') {
          // Execute the callback with trigger and modal elements
          const result = callbackFunction.call(this, triggerElement, modalElement);

          // If callback returns false, prevent modal from showing
          return result !== false;
        } else {
          console.warn(`Modal callback function '${callbackName}' not found in global scope`);
          return true; // Continue showing modal even if callback not found
        }
      }

      return true; // No callback specified, continue normally
    } catch (error) {
      console.error('Error executing modal callback:', error);
      return true; // Continue showing modal even if callback failed
    }
  }
}